import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { Run } from "../types";
import { fromUnix } from "helpers/dateFormat";

const STALE_AFTER_SEC = 90; // a run whose lastSeenAt is older than this is considered down

interface Segment {
    kind: "up" | "down";
    start: number; // unix s
    end: number;
    running?: boolean;
}

export function fmtDuration(secs: number): string {
    secs = Math.max(0, Math.floor(secs));
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60) % 60;
    const h = Math.floor(secs / 3600) % 24;
    const d = Math.floor(secs / 86400);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
    return `${m}m`;
}

function hhmm(unix: number): string {
    return fromUnix(unix).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// buildSegments turns runs into alternating up/down segments across [firstStart, now].
export function buildSegments(runs: Run[], now: number): Segment[] {
    const sorted = [...runs].sort((a, b) => a.startedAt - b.startedAt);
    const segs: Segment[] = [];
    sorted.forEach((r, i) => {
        const isLast = i === sorted.length - 1;
        const running = isLast && now - r.lastSeenAt <= STALE_AFTER_SEC;
        // A run counts as up until its stale grace expires, so downtime doesn't start
        // at lastSeenAt (which would overstate every stale run by STALE_AFTER_SEC).
        const staleAt = Math.min(now, r.lastSeenAt + STALE_AFTER_SEC);
        segs.push({ kind: "up", start: r.startedAt, end: running ? now : staleAt, running });
        if (!isLast) {
            const next = sorted[i + 1];
            if (next.startedAt > staleAt) segs.push({ kind: "down", start: staleAt, end: next.startedAt });
        } else if (!running && now > staleAt) {
            segs.push({ kind: "down", start: staleAt, end: now });
        }
    });
    return segs;
}

export default function Timeline({ runs }: { runs: Run[] }) {
    const theme = useTheme();
    const [hover, setHover] = useState<number | null>(null);

    if (runs.length === 0) {
        return <Typography variant="caption" color="text.secondary">No run history yet.</Typography>;
    }

    const now = Math.floor(Date.now() / 1000);
    const segs = buildSegments(runs, now);
    const winStart = Math.min(...runs.map(r => r.startedAt));
    const winEnd = now;
    const span = winEnd - winStart || 1;
    const pct = (t: number) => ((t - winStart) / span) * 100;

    const upColor = theme.palette.success.main;
    const downColor = theme.palette.error.main;

    // Which segment the detail line describes: the hovered one, or the last (most
    // recent) segment when nothing is hovered.
    const activeIdx = hover ?? segs.length - 1;
    const active = segs[activeIdx];

    return (
        <Box>
            <Box sx={{ position: "relative", height: 26, borderRadius: 1, overflow: "hidden", border: 1, borderColor: "divider", bgcolor: "action.hover" }}>
                {segs.map((s, i) => {
                    const left = pct(s.start);
                    const width = Math.max(0.3, pct(s.end) - left);
                    const isActive = i === activeIdx;
                    const label = `${s.kind === "up" ? (s.running ? "Up (running)" : "Up") : "Down"}, ${fmtDuration(s.end - s.start)}, ${hhmm(s.start)} to ${s.running ? "now" : hhmm(s.end)}`;
                    return (
                        <Box
                            key={i}
                            component="button"
                            type="button"
                            aria-label={label}
                            onMouseEnter={() => setHover(i)}
                            onMouseLeave={() => setHover(null)}
                            onFocus={() => setHover(i)}
                            onBlur={() => setHover(null)}
                            sx={{
                                position: "absolute", top: 0, bottom: 0,
                                left: `${left}%`, width: `${width}%`,
                                p: 0, border: "none", appearance: "none",
                                bgcolor: s.kind === "up" ? upColor : downColor,
                                opacity: isActive ? 1 : (s.kind === "up" ? (s.running ? 0.85 : 0.7) : 0.45),
                                cursor: "pointer",
                                transition: "opacity 0.1s",
                                "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
                            }}
                        />
                    );
                })}
            </Box>

            <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">{hhmm(winStart)}</Typography>
                <Typography variant="caption" color="text.secondary">now</Typography>
            </Stack>

            {active && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    <b style={{ color: active.kind === "up" ? upColor : downColor }}>
                        {active.kind === "up" ? (active.running ? "Up (running)" : "Up") : "Down"}
                    </b>
                    {" "}{fmtDuration(active.end - active.start)} · {hhmm(active.start)} → {active.running ? "now" : hhmm(active.end)}
                    {hover === null && <Typography component="span" variant="caption" color="text.disabled"> · latest</Typography>}
                </Typography>
            )}
        </Box>
    );
}
